/**
 * OpenStreetMap helpers used by the client-side places lookup: the Overpass query for
 * nearby food venues and parsers turning Overpass/Nominatim JSON into plain candidates.
 * No I/O here – the web app and the native shell do the fetching.
 */
export const FOOD_AMENITIES = [
    'restaurant',
    'cafe',
    'fast_food',
    'bar',
    'pub',
    'biergarten',
    'food_court',
    'ice_cream',
];
export const FOOD_SHOPS = ['bakery'];
export const NEARBY_RADIUS_M = 150;
export const NEARBY_RADIUS_WIDE_M = 500;
export function overpassNearbyQuery(center, radiusM, timeoutS = 8) {
    const around = `around:${Math.round(radiusM)},${center.lat.toFixed(6)},${center.lng.toFixed(6)}`;
    const amenity = FOOD_AMENITIES.join('|');
    const shop = FOOD_SHOPS.join('|');
    return [
        `[out:json][timeout:${timeoutS}];`,
        '(',
        `nwr(${around})["amenity"~"^(${amenity})$"]["name"];`,
        `nwr(${around})["shop"~"^(${shop})$"]["name"];`,
        ');',
        'out center tags 40;',
    ].join('');
}
function text(value, max) {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    return trimmed ? trimmed.slice(0, max) : null;
}
/** Accepts only absolute http(s) URLs; bare domains get an https:// prefix. */
export function normalizeWebsite(value) {
    const raw = text(value, 500);
    if (!raw)
        return null;
    const candidate = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
    try {
        const url = new URL(candidate);
        if (url.protocol !== 'http:' && url.protocol !== 'https:')
            return null;
        if (!url.hostname.includes('.'))
            return null;
        return url.toString();
    }
    catch {
        return null;
    }
}
/** "italian;pizza" → ["italian", "pizza"]; underscores become spaces. */
export function parseCuisineTag(value) {
    const raw = text(value, 400);
    if (!raw)
        return [];
    const out = [];
    for (const part of raw.split(/[;,]/)) {
        const cuisine = part.trim().toLowerCase().replace(/_/g, ' ').slice(0, 60);
        if (cuisine && !out.includes(cuisine))
            out.push(cuisine);
        if (out.length >= 12)
            break;
    }
    return out;
}
function countryCode(value) {
    const raw = text(value, 2);
    return raw && /^[A-Za-z]{2}$/.test(raw) ? raw.toUpperCase() : null;
}
function street(tags) {
    const name = text(tags['addr:street'] ?? tags['addr:place'], 160);
    const number = text(tags['addr:housenumber'], 20);
    if (!name)
        return null;
    return number ? `${name} ${number}` : name;
}
/** Parses an Overpass `out center tags` response. Malformed elements are skipped. */
export function parseOverpassResponse(json) {
    const elements = json?.elements;
    if (!Array.isArray(elements))
        return [];
    const out = [];
    for (const el of elements) {
        if (!el || typeof el !== 'object')
            continue;
        const type = el.type;
        if (type !== 'node' && type !== 'way' && type !== 'relation')
            continue;
        if (typeof el.id !== 'number' || !Number.isSafeInteger(el.id))
            continue;
        const lat = typeof el.lat === 'number' ? el.lat : el.center?.lat;
        const lng = typeof el.lon === 'number' ? el.lon : el.center?.lon;
        if (typeof lat !== 'number' || typeof lng !== 'number')
            continue;
        const tags = el.tags ?? {};
        const name = text(tags['name'], 200);
        if (!name)
            continue;
        out.push({
            ref: `${type}/${el.id}`,
            name,
            category: text(tags['amenity'] ?? tags['shop'], 60),
            cuisines: parseCuisineTag(tags['cuisine']),
            lat,
            lng,
            street: street(tags),
            postalCode: text(tags['addr:postcode'], 20),
            city: text(tags['addr:city'], 120),
            region: text(tags['addr:state'], 120),
            countryCode: countryCode(tags['addr:country']),
            website: normalizeWebsite(tags['contact:website'] ?? tags['website']),
            brand: text(tags['brand'] ?? tags['operator'], 120),
        });
    }
    return out;
}
function nominatimCity(address) {
    if (!address)
        return null;
    return text(address.city ?? address.town ?? address.village ?? address.municipality ?? address.hamlet, 120);
}
function nominatimStreet(address) {
    const road = text(address?.road ?? address?.pedestrian, 160);
    const number = text(address?.house_number, 20);
    if (!road)
        return null;
    return number ? `${road} ${number}` : road;
}
const OSM_TYPES = { N: 'node', W: 'way', R: 'relation' };
function osmType(value) {
    if (typeof value !== 'string')
        return null;
    if (value === 'node' || value === 'way' || value === 'relation')
        return value;
    return OSM_TYPES[value.toUpperCase()] ?? null;
}
/** Parses a Nominatim `/search?format=jsonv2&addressdetails=1&extratags=1` response. */
export function parseNominatimSearch(json) {
    if (!Array.isArray(json))
        return [];
    const out = [];
    for (const r of json) {
        if (!r || typeof r !== 'object')
            continue;
        const type = osmType(r.osm_type);
        const id = typeof r.osm_id === 'number' ? r.osm_id : Number(r.osm_id);
        const lat = Number(r.lat);
        const lng = Number(r.lon);
        if (!type || !Number.isSafeInteger(id) || !Number.isFinite(lat) || !Number.isFinite(lng))
            continue;
        const name = text(r.name, 200) ??
            text(typeof r.display_name === 'string' ? r.display_name.split(',')[0] : null, 200);
        if (!name)
            continue;
        out.push({
            ref: `${type}/${id}`,
            name,
            category: text(r.type, 60),
            cuisines: parseCuisineTag(r.extratags?.['cuisine']),
            lat,
            lng,
            street: nominatimStreet(r.address),
            postalCode: text(r.address?.postcode, 20),
            city: nominatimCity(r.address),
            region: text(r.address?.state ?? r.address?.county, 120),
            countryCode: countryCode(r.address?.country_code),
            website: normalizeWebsite(r.extratags?.['contact:website'] ?? r.extratags?.['website']),
        });
    }
    return out;
}
/** Parses a Nominatim `/reverse?format=jsonv2` response. */
export function parseNominatimReverse(json) {
    const address = json?.address;
    if (!address || typeof address !== 'object')
        return null;
    return {
        street: nominatimStreet(address),
        postalCode: text(address.postcode, 20),
        city: nominatimCity(address),
        region: text(address.state ?? address.county, 120),
        countryCode: countryCode(address.country_code),
    };
}
/**
 * Serializes calls so that at most one starts per `intervalMs` (Nominatim: 1 request/s).
 * `now` and `sleep` are injectable for tests.
 */
export function createThrottle(intervalMs, now = () => Date.now(), sleep = (ms) => new Promise((r) => setTimeout(r, ms))) {
    let chain = Promise.resolve();
    let last = -Infinity;
    return (task) => {
        const run = chain.then(async () => {
            const wait = last + intervalMs - now();
            if (wait > 0)
                await sleep(wait);
            last = now();
            return task();
        });
        chain = run.catch(() => undefined);
        return run;
    };
}
/** Back-off window after a throttling/blocked response (HTTP 429/403/504). */
export const OSM_BACKOFF_MS = 60_000;
export function isOsmThrottleStatus(status) {
    return status === 429 || status === 403 || status === 504 || status === 509;
}
//# sourceMappingURL=osm.js.map